import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hexagon, Check, Loader2, Sparkles } from "lucide-react";
import { csuiteApi } from "../lib/csuiteApi";
import { api } from "../lib/api";
import { useCSuiteAuth } from "../state/CSuiteAuthContext";
import { toast } from "../lib/utils";
import useSEO from "../lib/useSEO";
import SupportLink from "./SupportLink";

const PERKS = {
  starter: [
    "Portfolio Command Center · Executive Scorecard · S/4HANA cockpit",
    "Dashboard · Tracker · Requirements · Documents",
    "RAID · Actions · Budget · Stakeholders · Gap/Fit",
    "PDF · Word · PPT exports (with capittance.com branding)",
    "Capittance NAVBOT — executive AI · chat mode (Claude 4.5)",
    "Full autosave · shareable links · email-code verification",
  ],
  pro: [
    "Everything in Starter — plus Platinum tier",
    "Microsoft Dynamics 365 executive cockpit (Finance · Sales · SCM · CX)",
    "Value Realization Tracker · AI Insight Feed · Board Pack generator",
    "Governance · Fit-to-Standard · Data Migration · Integration · Testing · OCM · Cutover",
    "S/4HANA (134-step Activate) · Strategy framework · IP Library",
    "Capittance NAVBOT — proactive briefings + quick actions",
    "Teams webhooks · 3-tier escalation · priority support",
    "AI-generated SAP Activate document pipeline",
  ],
};

export default function Signup() {
  const nav = useNavigate();
  const { user, login } = useCSuiteAuth();
  useSEO({
    title: "Sign up · Start a 14-day Platinum Pilot | Capittance",
    description: "Create your Capittance Harmonizer account. Start a 14-day Platinum pilot at $0, or choose Starter at $200/mo. Cancel any time.",
    path: "/signup",
  });
  const [step, setStep] = useState(user ? "plan" : "signup"); // signup → verify → plan
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [uname, setUname] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("pro");
  const [params] = useSearchParams();

  // When arriving from /csuite/verify with ?next=/csuite/dashboard we pre-select
  // Platinum and remember the destination for post-payment routing.
  useEffect(() => {
    const nextTarget = params.get("next") || "";
    if (nextTarget) {
      try { sessionStorage.setItem("harmonizer_signup_next", nextTarget); } catch { /* noop */ }
    }
    const wantPlan = params.get("plan");
    if (wantPlan === "starter" || wantPlan === "pro") setPlan(wantPlan);
    else if (nextTarget.startsWith("/csuite")) setPlan("pro");
  }, [params]);

  useEffect(() => { if (user) setStep("plan"); }, [user]);

  const doSignup = async (e) => {
    e.preventDefault();
    if (!terms) return toast("Please accept the Terms & Agreements");
    if (!email || !pw || !uname) return toast("All fields required");
    setLoading(true);
    try {
      const r = await csuiteApi.signup({ username: uname, email, password: pw, role: "OPERATOR" });
      setDevCode(r.dev_code || "");
      setStep("verify");
      toast("Verification code sent to your email");
    } catch (err) {
      toast(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await csuiteApi.verify({ email, code });
      await login(email, pw);
      setStep("plan");
      toast("Verified! Choose your plan.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const doCheckout = async () => {
    setLoading(true);
    try {
      const nextTarget = (() => {
        try { return sessionStorage.getItem("harmonizer_signup_next") || ""; } catch { return ""; }
      })();
      const r = await api.subscribeCheckout(plan, nextTarget);
      window.location.href = r.checkout_url;
    } catch (err) {
      toast(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 brand-chip grid place-items-center"><Hexagon size={18} strokeWidth={2.2} /></div>
          <div className="font-display font-extrabold text-2xl tracking-tight">Harmonizer</div>
        </div>

        {step === "signup" && (
          <div className="max-w-md">
            <h1 className="font-display text-3xl font-extrabold tracking-tight mb-4">Create your account</h1>
            <form onSubmit={doSignup} className="space-y-3">
              <input className="fld" placeholder="Username" value={uname} onChange={e => setUname(e.target.value)} data-testid="signup-uname"/>
              <input className="fld" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} data-testid="signup-email"/>
              <input className="fld" type="password" placeholder="Password (min 8 chars)" value={pw} onChange={e => setPw(e.target.value)} data-testid="signup-pw"/>
              <label className="flex items-start gap-2 text-xs text-zinc-600 py-2">
                <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} className="mt-0.5" data-testid="signup-terms"/>
                <span>I accept the <a href="/terms" target="_blank" className="underline">Terms &amp; Agreements</a>. I understand subscriptions are <strong>non-refundable</strong>, I can cancel immediately at any time with <strong>no proration</strong>, and access ends at cancellation.</span>
              </label>
              <button className="btn btn-primary w-full" disabled={loading} data-testid="signup-submit">
                {loading ? <Loader2 size={14} className="animate-spin"/> : "Create account →"}
              </button>
            </form>
            <div className="text-xs text-zinc-500 mt-4">Already have an account? <a href="/login" className="underline">Sign in</a></div>
          </div>
        )}

        {step === "verify" && (
          <div className="max-w-md">
            <h1 className="font-display text-3xl font-extrabold tracking-tight mb-4">Verify your email</h1>
            <p className="text-sm text-zinc-500 mb-4">We sent a 6-digit code to <strong>{email}</strong>. Enter it below.</p>
            {devCode && <div className="font-mono text-xs bg-amber-50 border border-amber-200 rounded p-2 mb-3">Dev code: <strong>{devCode}</strong></div>}
            <form onSubmit={doVerify} className="space-y-3">
              <input className="fld font-mono tracking-widest text-center text-lg" placeholder="000000" maxLength={6} value={code} onChange={e => setCode(e.target.value)} data-testid="verify-code"/>
              <button className="btn btn-primary w-full" disabled={loading} data-testid="verify-submit">
                {loading ? <Loader2 size={14} className="animate-spin"/> : "Verify →"}
              </button>
            </form>
          </div>
        )}

        {step === "plan" && (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mb-2">Pick a plan</h1>
            <p className="text-zinc-500 mb-8">Cancel anytime. No refunds after signup. No proration.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { id: "starter", name: "Starter",  price: 200, perks: PERKS.starter },
                { id: "pro",     name: "Platinum", price: 350, perks: PERKS.pro, featured: true },
              ].map(p => (
                <div key={p.id} className={`card p-6 relative cursor-pointer transition-all ${plan === p.id ? "ring-2 ring-zinc-900" : ""} ${p.featured ? "border-zinc-900" : ""}`} onClick={() => setPlan(p.id)} data-testid={`plan-${p.id}`}>
                  {p.featured && <div className="absolute -top-2 right-4 pill" style={{ background: "#0369A1", color: "#fff", fontSize: 10 }}><Sparkles size={10} className="inline mr-1"/>Recommended</div>}
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.name}</div>
                  <div className="font-display text-4xl font-extrabold mt-2">${p.price}<span className="text-lg text-zinc-500">/mo</span></div>
                  <ul className="mt-4 space-y-1.5 text-sm">
                    {p.perks.map((k, i) => <li key={i} className="flex gap-2"><Check size={14} className="text-green-600 shrink-0 mt-0.5"/><span>{k}</span></li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button className="btn btn-primary" disabled={loading} onClick={doCheckout} data-testid="checkout-submit">
                {loading ? <Loader2 size={14} className="animate-spin"/> : `Subscribe to ${plan === "starter" ? "Starter · $200/mo" : "Platinum · $350/mo"}`}
              </button>
            </div>
          </>
        )}
        <div className="mt-10 pt-6 border-t border-[var(--border)] text-center text-zinc-500">
          <SupportLink />
        </div>
      </div>
    </div>
  );
}
